import { computed, ref, watch, ComputedRef, Ref } from 'vue';
import { ExpandedState, getExpandedRowModel, getGroupedRowModel } from '@tanstack/vue-table';
import { merge } from 'lodash-es';
import { useEnvStore } from '../core/useEnvStore';
import type { Emits, Props } from '../../modernGrid.setup';
import type {
  GridRowDataType,
  GridRawRowDataType,
  GridTableInstance,
  GridTableInstanceOptions,
  GridRowInstance,
  GridTreeViewOption,
  GridDetailRowOption,
  GridExpandingOption,
  GridGroupingOption,
  GridColumnMeta,
  ColumnVisibilityInfo,
  PaginationOption,
} from '../../modernGrid.types';
import {
  defaultTreeViewOption,
  defaultDetailRowOption,
  defaultExpandingOption,
  defaultGroupingOption,
} from '../../modernGrid.define';
import {
  getRawRows,
  deepCompare,
  getIsDefaultColumn,
  getComparisonData,
} from '../../modernGrid.utils';

export const useExpanding = <RowDataType extends GridRawRowDataType>({
  props,
  emit,
  table,
  rowsMapById,
  paginationInfo,
  columnIds,
  columnSettingList,
}: {
  props: Props<RowDataType>;
  emit: Emits<RowDataType>;
  table: Ref<GridTableInstance<RowDataType> | undefined>;
  rowsMapById: Ref<Map<string, GridRowDataType<RowDataType>> | undefined>;
  paginationInfo: ComputedRef<PaginationOption>;
  columnIds: ComputedRef<string[]>;
  columnSettingList: Ref<ColumnVisibilityInfo[]>;
}) => {
  const { getEnv } = useEnvStore(props.useDashboardStore ?? false);
  const treeViewInfo = computed<GridTreeViewOption>(() =>
    merge({}, defaultTreeViewOption, props.option?.treeView),
  );
  const detailRowInfo = computed<GridDetailRowOption>(() =>
    merge({}, defaultDetailRowOption, props.option?.detailRow),
  );
  const expandingInfo = computed<GridExpandingOption>(() =>
    merge({}, defaultExpandingOption, props.option?.expanding),
  );
  const groupingInfo = computed<GridGroupingOption>(() =>
    merge({}, defaultGroupingOption, props.option?.grouping),
  );
  const expandingKey = computed<string>(() => {
    const { use: useTreeView, subRowKey: treeSubRowKey } = treeViewInfo.value;
    const { use: useDetailRow, detailRowKey } = detailRowInfo.value;
    let key = '';
    if (useTreeView) {
      key = treeSubRowKey;
    } else if (useDetailRow) {
      key = detailRowKey;
    }
    return key;
  });
  const expandingColumn = ref<string>('');
  const expandedState = ref<ExpandedState>(
    expandingInfo.value.expandAllOnLoad && !detailRowInfo.value.use ? true : {},
  );
  const expandedRows = computed<RowDataType[]>({
    get: () => props.expandedRows || [],
    set: (rows: RowDataType[]) => emit('update:expanded-rows', rows),
  });

  const lastEmittedExpandedKeys = ref<Set<string>>(new Set());

  const updateExpandedState = async (
    updater: ExpandedState | ((state: ExpandedState) => ExpandedState),
  ) => {
    expandedState.value = typeof updater === 'function' ? updater(expandedState.value) : updater;
    const rawRows = getRawRows(expandedState.value, rowsMapById.value);
    lastEmittedExpandedKeys.value = new Set(
      rawRows.map((row) => JSON.stringify(getComparisonData(row, expandingKey.value))),
    );
    expandedRows.value = rawRows;
  };

  const expandingOptions = computed<Partial<GridTableInstanceOptions<RowDataType>>>(() => {
    const { use: useTreeView } = treeViewInfo.value || {};
    const { use: useDetailRow } = detailRowInfo.value || {};
    const { use: useRowGroping, groupedColumnMode } = groupingInfo.value || {};
    const { filterFromLeafRows, paginateExpandedRows } = expandingInfo.value || {};

    return {
      getExpandedRowModel: getExpandedRowModel(),
      getGroupedRowModel: getGroupedRowModel(),
      filterFromLeafRows,
      paginateExpandedRows: paginationInfo.value.use ? paginateExpandedRows : undefined,
      manualExpanding: !(useTreeView || useDetailRow || useRowGroping),
      enableExpanding: useTreeView || useDetailRow || useRowGroping,
      enableGrouping: useRowGroping,
      autoResetExpanded: false,
      groupedColumnMode,
      getSubRows: (row: GridRowDataType<RowDataType>) => {
        const subRowKey = expandingKey.value || '';
        return row[subRowKey];
      },
      getRowCanExpand: (row: GridRowInstance<RowDataType>) => {
        const subRowKey = expandingKey.value || '';
        return row.original ? row.original[subRowKey]?.length : row[subRowKey]?.length;
      },
      onExpandedChange: updateExpandedState,
    };
  });

  const setExpandingColumn = () => {
    const useExpandingOption = treeViewInfo.value.use || groupingInfo.value.use;

    if (!useExpandingOption) {
      expandingColumn.value = '';
      return;
    }

    const allColumns = table.value?.getAllColumns() || [];

    // expandColumn: true인 컬럼 우선 검색
    const expandColumnDef = allColumns.find((col) => {
      const meta = col.columnDef.meta as GridColumnMeta | undefined;
      return meta?.expandColumn === true && !getIsDefaultColumn(col.id) && col.getIsVisible();
    });

    // expandColumn이 없으면 기존 로직 (첫 번째 가시 컬럼)
    const { columnOrder: columnOrderState } = table.value?.getState() || {};
    const storedOrder = getEnv(props.envKey)?.columnOrder ?? columnOrderState;
    const columnOrder = storedOrder ?? columnIds.value;

    const firstValidColumnId =
      expandColumnDef?.id ||
      columnOrder.find((id: string) => {
        const column = allColumns.find((col) => col.id === id);
        return column && !getIsDefaultColumn(id) && column.getIsVisible();
      }) ||
      '';

    expandingColumn.value = firstValidColumnId || '';

    if (expandingColumn.value.length) {
      columnSettingList.value.forEach((item) => {
        if (item.text === expandingColumn.value) {
          item.fixed = true;
        } else {
          const columnInfo = table.value?.getAllColumns()?.find(({ id }) => id === item.text)
            ?.columnDef || {
            enableHiding: undefined,
          };
          item.fixed = columnInfo.enableHiding === false;
        }
      });
    }
  };

  const preserveExpandedState = (
    prevState: Record<string, boolean>,
    prevRowsMap: Map<string, GridRowDataType<RowDataType>> | undefined,
    newRowsMap: Map<string, GridRowDataType<RowDataType>> | undefined,
  ): Record<string, boolean> => {
    if (!prevRowsMap || !newRowsMap || Object.keys(prevState).length === 0) {
      return {};
    }

    const newState: Record<string, boolean> = {};

    const newDataToIdMap = new Map<string, string>();
    newRowsMap.forEach((rowData, rowId) => {
      const comparisonKey = JSON.stringify(getComparisonData(rowData, expandingKey.value));
      newDataToIdMap.set(comparisonKey, rowId);
    });

    const newSuffixToIdMap = new Map<string, string>();
    newRowsMap.forEach((_, rowId) => {
      const colonIdx = rowId.indexOf(':');
      const suffix = colonIdx >= 0 ? rowId.substring(colonIdx + 1) : rowId;
      newSuffixToIdMap.set(suffix, rowId);
    });

    Object.entries(prevState).forEach(([prevRowId, isExpanded]) => {
      if (!isExpanded) return;

      const prevRowData = prevRowsMap.get(prevRowId);
      if (!prevRowData) return;

      if (newRowsMap.has(prevRowId)) {
        newState[prevRowId] = true;
        return;
      }

      const comparisonKey = JSON.stringify(getComparisonData(prevRowData, expandingKey.value));
      const newRowId = newDataToIdMap.get(comparisonKey);
      if (newRowId) {
        newState[newRowId] = true;
        return;
      }

      const cidx = prevRowId.indexOf(':');
      const suffix = cidx >= 0 ? prevRowId.substring(cidx + 1) : prevRowId;
      const newRowIdFromSuffix = newSuffixToIdMap.get(suffix);
      if (newRowIdFromSuffix) {
        newState[newRowIdFromSuffix] = true;
      }
    });

    return newState;
  };

  const isExpandedRowsExternallyChanged = (): boolean => {
    const currentRows = props.expandedRows;
    if (!currentRows || currentRows.length === 0) {
      return lastEmittedExpandedKeys.value.size > 0;
    }
    const currentKeys = new Set(
      currentRows.map((row) => JSON.stringify(getComparisonData(row, expandingKey.value))),
    );
    if (currentKeys.size !== lastEmittedExpandedKeys.value.size) return true;
    for (const key of currentKeys) {
      if (!lastEmittedExpandedKeys.value.has(key)) return true;
    }
    return false;
  };

  const buildStateFromExpandedRows = (rows: RowDataType[]): Record<string, boolean> => {
    const stateByRows: Record<string, boolean> = {};
    if (!rows.length) return stateByRows;
    const dataToIdMap = new Map<string, string>();
    rowsMapById.value?.forEach((rowData, rowId) => {
      const comparisonData = getComparisonData(rowData, expandingKey.value);
      dataToIdMap.set(JSON.stringify(comparisonData), rowId);
    });
    rows.forEach((row) => {
      const comparisonKey = JSON.stringify(getComparisonData(row, expandingKey.value));
      const rowId = dataToIdMap.get(comparisonKey);
      if (rowId) stateByRows[rowId] = true;
    });
    return stateByRows;
  };

  const setExpandingState = (prevRowsMap?: Map<string, GridRowDataType<RowDataType>>) => {
    let newState = expandedState.value;

    const isExternalChange =
      !prevRowsMap && props.expandedRows !== undefined && isExpandedRowsExternallyChanged();

    if (
      prevRowsMap &&
      typeof expandedState.value === 'object' &&
      Object.keys(expandedState.value).length > 0
    ) {
      newState = preserveExpandedState(
        expandedState.value as Record<string, boolean>,
        prevRowsMap,
        rowsMapById.value,
      );
    } else if (prevRowsMap && expandedState.value === true) {
      newState = true;
    } else if (isExternalChange) {
      newState = buildStateFromExpandedRows(props.expandedRows!);
    } else if (props.expandedRows?.length) {
      newState = buildStateFromExpandedRows(props.expandedRows);
    } else if (expandingInfo.value.expandAllOnLoad && !detailRowInfo.value.use) {
      newState = true;
    }

    if (
      expandingInfo.value.expandAllOnLoad &&
      !detailRowInfo.value.use &&
      typeof newState === 'object' &&
      rowsMapById.value
    ) {
      const stateObj = newState as Record<string, boolean>;
      const prevRowIdSet = prevRowsMap ? new Set(prevRowsMap.keys()) : null;
      const prevSuffixSet = new Set<string>();
      if (prevRowsMap) {
        prevRowsMap.forEach((_, prevId) => {
          const colonIdx = prevId.indexOf(':');
          prevSuffixSet.add(colonIdx >= 0 ? prevId.substring(colonIdx + 1) : prevId);
        });
      }
      rowsMapById.value.forEach((rowData, rowId) => {
        if (!(rowId in stateObj)) {
          let isExistingRow = false;
          if (prevRowIdSet) {
            if (prevRowIdSet.has(rowId)) {
              isExistingRow = true;
            } else {
              const colonIdx = rowId.indexOf(':');
              const suffix = colonIdx >= 0 ? rowId.substring(colonIdx + 1) : rowId;
              isExistingRow = prevSuffixSet.has(suffix);
            }
          }
          if (!isExistingRow) {
            stateObj[rowId] = true;
          }
        }
      });
    }

    expandedState.value = newState;
    table.value?.setExpanded(newState);
    setExpandingColumn();
  };

  const setGroupingState = () => {
    const { use, groupByColumns } = groupingInfo.value;
    if (!use || !groupByColumns.length) {
      table.value?.resetGrouping();
    } else {
      table.value?.setGrouping(groupByColumns || []);
    }
    if (expandingInfo.value.expandAllOnLoad && !detailRowInfo.value.use) {
      expandedState.value = true;
      table.value?.setExpanded(true);
    }
  };

  watch(
    () => groupingInfo.value,
    (groupingInfoValue, oldGroupingValue) => {
      if (!deepCompare(groupingInfoValue, oldGroupingValue)) {
        setGroupingState();
      }
    },
    { deep: true },
  );

  return {
    treeViewInfo,
    detailRowInfo,
    expandingInfo,
    groupingInfo,
    expandingKey,
    expandingColumn,
    expandedState,
    expandingOptions,
    setExpandingState,
    setGroupingState,
    isExpandedRowsExternallyChanged,
  };
};
